import { getLocalStorage, getSessionStorage } from '@shared/core';
import qs from '@shared/core/queryString';
import { v4 as uuid } from 'uuid';

const BROWSER_ID_STORAGE_KEY = 'browserId';
const SESSION_ID_STORAGE_KEY = 'joySessionId';
const SEGMENT_SESSION_ID_KEY = 'analytics_session_id';

/**
 * The `browserId` is unique to each browser and persisted to local storage.
 * It allows us to track a user’s journey across multiple tabs within the same browser.
 * Can be combined with `sessionId` and `instanceId` to understand user interactions.
 */
export const getBrowserId = () => {
  const storage = getLocalStorage();
  let browserId = storage.getItem(BROWSER_ID_STORAGE_KEY);

  if (!browserId) {
    // Ensure browserId exists + is persisted
    browserId = uuid();
    storage.setItem(BROWSER_ID_STORAGE_KEY, browserId);
  }

  return browserId;
};

let sessionId: string | null | undefined;

/**
 * The `sessionId` is unique to each browser tab and persisted to session storage.
 * This is useful to understanding actions a user took within a single tab.
 */
export const getSessionId = (): string => {
  if (sessionId) {
    return sessionId;
  }

  const sessionStorage = getSessionStorage();
  sessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);

  if (!sessionId) {
    sessionId = uuid();
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
  }

  return sessionId;
};

/**
 * Get the session ID for Amplitude session tracking.
 * Synchronized with Segment's analytics_session_id for consistent session tracking.
 * Returns the session ID (timestamp in milliseconds when the session started).
 */
export const getAmplitudeSessionId = (): number => {
  const localStorage = getLocalStorage();
  const segmentSessionId = localStorage.getItem(SEGMENT_SESSION_ID_KEY);

  if (segmentSessionId) {
    return parseInt(segmentSessionId, 10);
  }

  // Segment's session ID is the session start time in milliseconds since epoch
  const now = Date.now();
  localStorage.setItem(SEGMENT_SESSION_ID_KEY, now.toString());
  return now;
};

// this is a "best effort", and will break when running the test suite
const _instanceId = (qs && qs.getValueString('instanceId')) || uuid();

/**
 * The `instanceId` identifies the current load of joy-web.
 * This is different from sessionId because instanceId is regenerated on every reload, even
 * in the same tab, whereas sessionId is only regenerated when a new tab is opened.
 */
export const getInstanceId = () => {
  return _instanceId;
};
